import { Injectable, OnDestroy, computed, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of, Subject } from 'rxjs';
import { map, switchMap, catchError, shareReplay, distinctUntilChanged, debounceTime, takeUntil, tap, finalize, filter, take, pairwise, startWith } from 'rxjs/operators';
import { Property } from '@shared/definitions/property';
import { Contract } from '@shared/definitions/contract';
import { UnifiedFinancialCalculationService, PropertyFinancialAnalysis } from '@core/services/finance/unified-financial-calculation.service';
import { ExpenseBreakdown, expenseBreakdownOf } from '@core/services/data/expense-breakdown';
import { PropertyFinancialStateService } from '@core/services/finance/property-financial-state.service';
import { AppCoordinationService } from '@core/services/infra/app-coordination.service';
import { PropertyStore } from '@shared/services/property.store';
import { ContractStore } from '@shared/services/contract.store';
import { toObservable } from '@angular/core/rxjs-interop';

export interface PropertyListFinancials {
  propertyUID: string;
  currentValue: number;
  purchasePrice: number;
  monthlyRent: number;
  actualMonthlyRent: number; // Actual rent from contracts (0 if vacant)
  yearlyRent: number;
  totalDebt: number;
  equity: number;
  cashFlow: number;
  grossYield: number;
  capRate: number;
  cashOnCashReturn: number;
  roi: number;
  hasValidData: boolean;
  hasActiveContract: boolean;
  dataSource: 'real' | 'estimated' | 'mixed';
  isRentEstimated: boolean; // True if rent is estimated
  lastCalculated: Date;
  
  // ✅ NEW: Real expense data from StandardizedKPICalculatorService (annual amounts)
  totalOperatingExpenses: number; // Annual operating expenses from settings
  totalDebtService: number; // Annual debt service from loan calculations
  /**
   * Les charges d'exploitation rangées par nature (montants annuels) et celles
   * que le moteur a dû estimer faute de saisie. Absent d'un bien dont le calcul
   * a échoué : on n'invente pas de répartition.
   */
  expenseBreakdown?: ExpenseBreakdown;
}

export interface PortfolioSummary {
  totalValue: number;
  totalMonthlyRent: number;
  totalMonthlyIncome: number;
  averageGrossYield: number;
  averageCapRate: number;
  propertyCount: number;
  totalProperties: number;
  rentedProperties: number;
  occupancyRate: number;
  dataQuality: 'high' | 'medium' | 'low';
  lastUpdated: Date;
}

@Injectable({
  providedIn: 'root'
})
export class PropertyListStateService implements OnDestroy {
  private propertiesSubject = new BehaviorSubject<Property[]>([]);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);
  
  // Cache management
  private financialCache = new Map<string, PropertyListFinancials>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  
  private destroy$ = new Subject<void>();
  private refreshTrigger$ = new BehaviorSubject<boolean>(true);
  
  // Debounced property loading to prevent excessive calls
  private propertiesSubject$ = new BehaviorSubject<Property[]>([]);

  private readonly contractStore = inject(ContractStore);

  constructor(
    private unifiedCalculationService: UnifiedFinancialCalculationService,
    private propertyFinancialStateService: PropertyFinancialStateService,
    private appCoordination: AppCoordinationService,
    private propertyStore: PropertyStore
  ) {
    this.initializeOptimizedStreams();
    this.connectToPropertyService();
    this.watchContractChanges();
  }
  
  private initializeOptimizedStreams(): void {
    
    // Optimized property loading with intelligent change detection and coordination
    this.propertiesSubject.pipe(
      distinctUntilChanged((prev: Property[], curr: Property[]) => {
        // Quick length check first
        if (prev.length !== curr.length) return false;
        
        // Optimized comparison - check IDs and update timestamps only
        return prev.every((prevProp, index) => {
          const currProp = curr[index];
          return prevProp.propertyUID === currProp.propertyUID && 
                 prevProp.updatedAt?.getTime() === currProp.updatedAt?.getTime();
        });
      }),
      // Process properties immediately when available
      filter(properties => {
        // Only process if we have meaningful data
        const hasValidProperties = properties.length > 0 && 
          properties.some(p => p.propertyUID && p.name);
        
        if (hasValidProperties) {
          console.log(`📊 PropertyListStateService: Processing ${properties.length} properties`);
        }
        return hasValidProperties;
      }),
      // Minimal debouncing for immediate response
      debounceTime(50), // Reduced from 500ms to 50ms for immediate response
      tap(properties => {
        console.log(`📊 PropertyListStateService: Processing ${properties.length} properties`);
        // Only set loading if we don't already have cached data
        const currentProperties = this.propertiesSubject$.value;
        if (currentProperties.length === 0) {
          this.isLoadingSubject.next(properties.length > 0);
        }
      }),
      takeUntil(this.destroy$)
    ).subscribe((properties: Property[]) => {
      this.propertiesSubject$.next(properties);
    });
  }
  
  // #441 lot 1: memoized so every caller shares one pipeline instead of each
  // access building its own shareReplay (which then tore itself down and
  // reran the whole enrichment whenever its subscriber count hit zero).
  private _enrichedProperties$: Observable<PropertyListFinancials[]> | null = null;

  /**
   * Get enriched properties with financial data - optimized with intelligent caching
   */
  get enrichedProperties$(): Observable<PropertyListFinancials[]> {
    return (this._enrichedProperties$ ??= this.propertiesSubject$.pipe(
      distinctUntilChanged((prev: Property[], curr: Property[]) => {
        // More efficient comparison
        if (prev.length !== curr.length) return false;
        return prev.every((p, i) => p.propertyUID === curr[i].propertyUID && 
                                   p.updatedAt?.getTime() === curr[i].updatedAt?.getTime());
      }),
      // Minimal debounce for immediate response
      debounceTime(50), // Reduced from 200ms to 50ms for immediate response
      // tap(properties => {
      //   if (properties.length > 0) {
      //     console.log(`🔄 PropertyListStateService: Enriching ${properties.length} properties...`);
      //   }
      // }),
      switchMap((properties: Property[]) => {
        if (properties.length === 0) {
          this.isLoadingSubject.next(false);
          this.errorSubject.next(null);
          // No properties to enrich — mark the financial-calculations step done
          // so the authenticated loading screen doesn't wait forever on an
          // enrichment that never runs (empty/new workspace stalled at ~66%).
          this.appCoordination.markServiceLoaded('financialCalculations');
          return of([]);
        }
        
        this.errorSubject.next(null);
        
        // Intelligent cache management
        const { cached, uncached } = this.partitionPropertiesByCache(properties);
        
        // console.log(`📊 Cache status: ${cached.length} cached, ${uncached.length} need calculation`);
        
        if (uncached.length === 0) {
          // All properties are cached - no need to show loading
          this.isLoadingSubject.next(false);
          // Everything already cached — the enrichment branch below (which marks
          // financialCalculations) won't run, so mark it here too.
          this.appCoordination.markServiceLoaded('financialCalculations');
          return of(cached);
        }
        
        // Only set loading if we actually need to calculate new data
        this.isLoadingSubject.next(true);
        
        // Process uncached properties in optimized batches
        return this.batchProcessPropertiesOptimized(uncached).pipe(
          map(newResults => {
            // Combine cached and new results, maintaining order
            const allResults = this.combineAndSortResults(properties, cached, newResults);
            // console.log(`✅ PropertyListStateService: Enriched ${allResults.length} properties`);
            
            // Mark financial calculations as complete in coordination service
            this.appCoordination.markServiceLoaded('financialCalculations');
            
            return allResults;
          }),
          catchError(error => {
            console.error('❌ PropertyListStateService: Error enriching properties:', error);
            this.errorSubject.next('Failed to calculate property financials');
            
            // Mark financial calculations as failed in coordination service
            this.appCoordination.markServiceFailed('financialCalculations', error.message);
            
            // Return cached results + fallback for failed ones
            const fallbackResults = uncached.map(p => this.getFallbackFinancials(p));
            return of([...cached, ...fallbackResults]);
          }),
          finalize(() => {
            this.isLoadingSubject.next(false);
          })
        );
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
      takeUntil(this.destroy$)
    ));
  }

  // Other methods remain the same...
  get properties$(): Observable<Property[]> {
    return this.propertiesSubject.asObservable();
  }
  
  get isLoading$(): Observable<boolean> {
    return this.isLoadingSubject.asObservable();
  }
  
  get error$(): Observable<string | null> {
    return this.errorSubject.asObservable();
  }
  
  /**
   * Calculate property financials with caching
   */
  private calculatePropertyFinancials(property: Property): Observable<PropertyListFinancials> {
    const cacheKey = property.propertyUID;
    
    // Check cache first
    if (this.isCachedValid(cacheKey)) {
      return of(this.financialCache.get(cacheKey)!);
    }
    
    return this.unifiedCalculationService.getPropertyFinancialAnalysis(property.propertyUID).pipe(
      map((analysis: PropertyFinancialAnalysis) => {
        const financials = this.transformToListFinancials(property, analysis);
        this.cacheFinancials(cacheKey, financials);
        return financials;
      }),
      catchError(error => {
        console.error(`[PropertyListStateService] ❌ Error calculating financials for ${property.propertyUID}:`, error);
        return of(this.getFallbackFinancials(property));
      })
    );
  }
  
  private transformToListFinancials(property: Property, analysis: PropertyFinancialAnalysis): PropertyListFinancials {
    // Use actualGrossAnnualIncome for actual rent (excludes estimated values)
    const actualMonthlyRent = (analysis?.kpis?.actualGrossAnnualIncome || 0) / 12;
    const isRentEstimated = analysis?.kpis?.isRentEstimated || false;
    
    return {
      propertyUID: property.propertyUID,
      currentValue: property.financials.currentValue || analysis?.unifiedData?.currentValue?.amount || 0,
      purchasePrice: analysis?.unifiedData?.purchasePrice?.amount || property.financials.purchaseInfo.price || 0,
      monthlyRent: (analysis?.kpis?.grossAnnualIncome || 0) / 12,
      actualMonthlyRent, // Store actual rent separately
      yearlyRent: analysis?.kpis?.grossAnnualIncome || 0,
      totalDebt: analysis?.kpis?.totalLoanBalance || 0,
      equity: analysis?.kpis?.equity || 0,
      cashFlow: analysis?.kpis?.netCashFlow || 0,
      grossYield: analysis?.kpis?.grossYield || 0,
      capRate: analysis?.kpis?.capRate || 0,
      cashOnCashReturn: analysis?.kpis?.cashOnCashReturn || 0,
      roi: analysis?.kpis?.cashOnCashReturn || 0,
      hasValidData: analysis?.dataQuality?.quality !== 'low',
      hasActiveContract: actualMonthlyRent > 0, // Only true if has actual rent
      dataSource: analysis?.dataQuality?.quality !== 'low' ? 'real' : 'estimated',
      isRentEstimated,
      lastCalculated: new Date(),
      
      // ✅ NEW: Extract real expense data from KPI calculations
      totalOperatingExpenses: analysis?.kpis?.totalOperatingExpenses || 0, // Annual operating expenses
      totalDebtService: analysis?.kpis?.totalDebtService || 0, // Annual debt service
      expenseBreakdown: expenseBreakdownOf(analysis?.unifiedData)
    };
  }
  
  private getFallbackFinancials(property: Property): PropertyListFinancials {
    return {
      propertyUID: property.propertyUID,
      currentValue: property.financials.currentValue || 0,
      purchasePrice: property.financials.purchaseInfo.price || 0,
      monthlyRent: 0,
      actualMonthlyRent: 0,
      yearlyRent: 0,
      totalDebt: 0,
      equity: property.financials.currentValue || 0,
      cashFlow: 0,
      grossYield: 0,
      capRate: 0,
      cashOnCashReturn: 0,
      roi: 0,
      hasValidData: false,
      hasActiveContract: false,
      dataSource: 'estimated',
      isRentEstimated: false,
      lastCalculated: new Date(),
      
      // ✅ NEW: Fallback expense values
      totalOperatingExpenses: 0,
      totalDebtService: 0
    };
  }
  
  private isCachedValid(propertyId: string): boolean {
    const cached = this.financialCache.get(propertyId);
    return cached ? cached.lastCalculated.getTime() + this.CACHE_DURATION > Date.now() : false;
  }
  
  private cacheFinancials(propertyId: string, financials: PropertyListFinancials): void {
    this.financialCache.set(propertyId, financials);
  }
  
  private arraysEqual(a: Property[], b: Property[]): boolean {
    return a.length === b.length && a.every((prop, index) => prop.propertyUID === b[index].propertyUID);
  }
  
  /**
   * Update properties list
   */
  updateProperties(properties: Property[]): void {
    this.propertiesSubject.next(properties);
  }
  
  /**
   * Set properties list (backward compatibility method)
   */
  setProperties(properties: Property[]): void {
    this.updateProperties(properties);
  }
  
  /**
   * Add property to the list. Idempotent on `propertyUID` so the optimistic
   * insert from the create-property flow does not race with the Firestore
   * listener in `connectToPropertyService` and produce a duplicate row.
   */
  addProperty(property: Property): void {
    const currentProperties = this.propertiesSubject.value;
    if (currentProperties.some(p => p.propertyUID === property.propertyUID)) {
      return;
    }
    this.propertiesSubject.next([...currentProperties, property]);
  }
  
  /**
   * Remove property from the list
   */
  removeProperty(propertyId: string): void {
    const currentProperties = this.propertiesSubject.value;
    const filteredProperties = currentProperties.filter(p => p.propertyUID !== propertyId);
    this.propertiesSubject.next(filteredProperties);
    
    // Remove from cache
    this.financialCache.delete(propertyId);
  }
  
  /**
   * Update specific property
   */
  updateProperty(updatedProperty: Property): void {
    const currentProperties = this.propertiesSubject.value;
    const index = currentProperties.findIndex(p => p.propertyUID === updatedProperty.propertyUID);
    
    if (index !== -1) {
      const newProperties = [...currentProperties];
      newProperties[index] = updatedProperty;
      this.propertiesSubject.next(newProperties);
      
      // Invalidate cache for this property
      this.financialCache.delete(updatedProperty.propertyUID);
    } else {
      // Add if not found
      const currentProperties = this.propertiesSubject.value;
      this.propertiesSubject.next([...currentProperties, updatedProperty]);
    }
  }
  
  /**
   * Optimized batch processing with intelligent batching and error handling
   */
  private batchProcessPropertiesOptimized(properties: Property[]): Observable<PropertyListFinancials[]> {
    if (properties.length === 0) {
      return of([]);
    }

    console.log(`🔄 Processing ${properties.length} uncached properties...`);
    
    // Process in smaller batches to prevent overwhelming the system
    const BATCH_SIZE = 5;
    const batches: Property[][] = [];
    
    for (let i = 0; i < properties.length; i += BATCH_SIZE) {
      batches.push(properties.slice(i, i + BATCH_SIZE));
    }

    // Process batches sequentially to avoid overwhelming the calculation service
    return combineLatest(
      batches.map(batch => this.processBatch(batch))
    ).pipe(
      map(batchResults => batchResults.flat()),
      tap(() => {
      })
    );
  }

  /**
   * Process a single batch of properties
   */
  private processBatch(properties: Property[]): Observable<PropertyListFinancials[]> {
    const calculations = properties.map(property => 
      this.unifiedCalculationService.getPropertyFinancialAnalysis(property.propertyUID).pipe(
        map(analysis => this.transformToListFinancials(property, analysis)),
        tap(result => {
          // Cache the result immediately
          this.cacheResult(result);
          console.log(`💾 Cached financials for property ${property.propertyUID}`);
        }),
        catchError(error => {
          console.error(`❌ Error calculating for property ${property.propertyUID}:`, error);
          const fallback = this.getFallbackFinancials(property);
          // Cache fallback too to avoid repeated failures
          this.cacheResult(fallback);
          return of(fallback);
        })
      )
    );
    
    return combineLatest(calculations);
  }

  /**
   * Partition properties into cached and uncached
   */
  private partitionPropertiesByCache(properties: Property[]): { cached: PropertyListFinancials[], uncached: Property[] } {
    const cached: PropertyListFinancials[] = [];
    const uncached: Property[] = [];
    
    properties.forEach(property => {
      const cachedResult = this.getCachedFinancials(property.propertyUID);
      if (cachedResult) {
        cached.push(cachedResult);
      } else {
        uncached.push(property);
      }
    });
    
    return { cached, uncached };
  }

  /**
   * Get cached financials for a property
   */
  private getCachedFinancials(propertyId: string): PropertyListFinancials | null {
    const cached = this.financialCache.get(propertyId);
    if (cached && this.isCachedValid(propertyId)) {
      return cached;
    }
    
    // Remove expired cache entries
    if (cached && !this.isCachedValid(propertyId)) {
      this.financialCache.delete(propertyId);
    }
    
    return null;
  }

  /**
   * Combine cached and new results while maintaining original property order
   */
  private combineAndSortResults(
    originalProperties: Property[], 
    cached: PropertyListFinancials[], 
    newResults: PropertyListFinancials[]
  ): PropertyListFinancials[] {
    const allResults = [...cached, ...newResults];
    
    // Sort results to match original property order
    return originalProperties.map(property => {
      const result = allResults.find(r => r.propertyUID === property.propertyUID);
      return result || this.getFallbackFinancials(property);
    });
  }
  
  private calculatePortfolioSummary(enrichedProperties: PropertyListFinancials[]): PortfolioSummary {
    // Get the original properties for basic KPI calculation
    const properties = this.propertiesSubject.value;
    
    // Calculate total value from ENRICHED data so it falls back to purchase
    // price / unified valuation when currentValue is unset — consistent with the
    // per-property rows and the yield weighting below (which already use p.currentValue).
    const totalValue = enrichedProperties.reduce((sum, p) => sum + (p.currentValue || 0), 0);
    
    // ✅ CRITICAL FIX: Use enriched data which has CORRECT rent from contract extraction
    // The enriched data uses DataNormalizationService which properly extracts rent from active contracts
    const totalMonthlyRent = enrichedProperties.reduce((sum, p) => {
      // Use actualMonthlyRent which comes from active contracts (not estimates)
      const rent = p.actualMonthlyRent || 0;
      return sum + rent;
    }, 0);
    
    // Count rented properties based on enriched data (has active contract flag)
    const rentedProperties = enrichedProperties.filter(p => p.hasActiveContract).length;
    
    const totalProperties = properties.length;
    const occupancyRate = totalProperties > 0 ? (rentedProperties / totalProperties) * 100 : 0;
    
    // Use enriched data for performance metrics (yields, cap rate)
    const averageGrossYield = totalValue > 0 ? 
      enrichedProperties.reduce((sum, p) => sum + (p.grossYield * p.currentValue), 0) / totalValue : 0;
    
    const averageCapRate = totalValue > 0 ?
      enrichedProperties.reduce((sum, p) => sum + (p.capRate * p.currentValue), 0) / totalValue : 0;
    
    // Data quality assessment from enriched data
    const highQualityCount = enrichedProperties.filter(p => p.hasValidData).length;
    const overallQuality: 'high' | 'medium' | 'low' = 
      highQualityCount / enrichedProperties.length > 0.7 ? 'high' :
      highQualityCount / enrichedProperties.length > 0.3 ? 'medium' : 'low';
    
    console.log('[PropertyListStateService] Portfolio Summary:', {
      totalProperties,
      rentedProperties,
      totalValue,
      totalMonthlyRent,
      occupancyRate: `${occupancyRate.toFixed(1)}%`
    });
    
    return {
      totalValue,
      totalMonthlyRent,
      totalMonthlyIncome: totalMonthlyRent,
      averageGrossYield,
      averageCapRate,
      propertyCount: totalProperties,
      totalProperties,
      rentedProperties,
      occupancyRate,
      dataQuality: overallQuality,
      lastUpdated: new Date()
    };
  }
  
  // Cache management methods
  private isCached(propertyId: string): boolean {
    const cached = this.financialCache.get(propertyId);
    return cached ? cached.lastCalculated.getTime() + this.CACHE_DURATION > Date.now() : false;
  }
  
  private getCachedResults(properties: Property[]): PropertyListFinancials[] {
    return properties
      .map(p => this.financialCache.get(p.propertyUID))
      .filter((cached): cached is PropertyListFinancials => 
        cached !== undefined && cached.lastCalculated.getTime() + this.CACHE_DURATION > Date.now())
      .map(cached => cached);
  }
  
  private cacheResult(result: PropertyListFinancials): void {
    this.financialCache.set(result.propertyUID, result);
  }
  
  // #441 lot 1: same memoization as enrichedProperties$ above.
  private _portfolioSummary$: Observable<PortfolioSummary> | null = null;

  /**
   * Get portfolio summary with intelligent caching
   */
  get portfolioSummary$(): Observable<PortfolioSummary> {
    return (this._portfolioSummary$ ??= this.enrichedProperties$.pipe(
      distinctUntilChanged((prev: PropertyListFinancials[], curr: PropertyListFinancials[]) => 
        prev.length === curr.length && 
        prev.every((p, i) => p.propertyUID === curr[i].propertyUID && p.lastCalculated.getTime() === curr[i].lastCalculated.getTime())
      ),
      debounceTime(50), // Minimal debounce for portfolio calculations
      map((enrichedProperties: PropertyListFinancials[]) => {
        if (enrichedProperties.length === 0) {
          return this.getEmptyPortfolioSummary();
        }
        
        const summary = this.calculatePortfolioSummary(enrichedProperties);
        return summary;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
      takeUntil(this.destroy$)
    ));
  }

  private getEmptyPortfolioSummary(): PortfolioSummary {
    return {
      totalValue: 0,
      totalMonthlyRent: 0,
      totalMonthlyIncome: 0,
      averageGrossYield: 0,
      averageCapRate: 0,
      propertyCount: 0,
      totalProperties: 0,
      rentedProperties: 0,
      occupancyRate: 0,
      dataQuality: 'low',
      lastUpdated: new Date()
    };
  }
  
  /**
   * Force refresh of all calculations
   */
  refreshCalculations(): void {
    this.clearAllCache();
    this.refreshTrigger$.next(true);
  }
  
  /**
   * Clear all cached calculations
   */
  clearAllCache(): void {
    this.financialCache.clear();
  }
  
  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.financialCache.size,
      keys: Array.from(this.financialCache.keys())
    };
  }
  
  /**
   * Connect to PropertyService to automatically receive property updates
   */
  private connectToPropertyService(): void {
    // Only connect once the app coordination service indicates services are loaded
    this.appCoordination.servicesLoaded$.pipe(
      filter(loaded => loaded),
      take(1),
      switchMap(() => this.propertyList$),
      distinctUntilChanged((prev: Property[], curr: Property[]) => {
        if (prev.length !== curr.length) return false;
        return prev.every((p, i) => p.propertyUID === curr[i].propertyUID && 
                                   p.updatedAt?.getTime() === curr[i].updatedAt?.getTime());
      }),
      takeUntil(this.destroy$)
    ).subscribe(properties => {
      console.log(`📊 PropertyListStateService: Auto-updating with ${properties.length} properties from PropertyService`);
      this.propertiesSubject.next(properties);
    });
  }

  /**
   * Invalidate cache for a specific property
   */
  invalidatePropertyCache(propertyUID: string): void {
    this.financialCache.delete(propertyUID);
  }

  /**
   * Watch contract-list changes and invalidate the enriched-financials cache for
   * any property whose contract-status fingerprint changed. Needed because the
   * per-property cache is keyed on the Property document's `updatedAt` — not on
   * contract mutations (termination, creation, rent change). Without this, the
   * list view keeps showing a property as "rented" for up to 5 minutes after
   * its last active contract is terminated.
   */
  // Cached signal-to-observable bridges (constructor injection context).
  private readonly propertyList$ = toObservable(computed(() => [...this.propertyStore.list()]));
  private readonly contractList$ = toObservable(computed(() => [...this.contractStore.list()]));

  private watchContractChanges(): void {
    this.contractList$.pipe(
      map(contracts => this.buildContractFingerprint(contracts)),
      distinctUntilChanged((a, b) => this.fingerprintsEqual(a, b)),
      startWith(new Map<string, string>()),
      pairwise(),
      takeUntil(this.destroy$)
    ).subscribe(([prev, curr]) => {
      const affectedProperties = new Set<string>();

      // Any property whose fingerprint changed (or appeared/disappeared) is dirty.
      curr.forEach((sig, propertyUID) => {
        if (prev.get(propertyUID) !== sig) affectedProperties.add(propertyUID);
      });
      prev.forEach((_sig, propertyUID) => {
        if (!curr.has(propertyUID)) affectedProperties.add(propertyUID);
      });

      if (affectedProperties.size === 0) return;

      affectedProperties.forEach(uid => this.financialCache.delete(uid));

      // Nudge the stream so subscribers recompute with the cache gaps.
      this.propertiesSubject$.next(this.propertiesSubject$.value);
    });
  }

  /** Per-property fingerprint = sorted "contractUID:status" entries. */
  private buildContractFingerprint(contracts: readonly Contract[]): Map<string, string> {
    const map = new Map<string, string[]>();
    for (const c of contracts) {
      if (!c.propertyUID) continue;
      const entry = `${c.contractUID}:${c.status}`;
      const existing = map.get(c.propertyUID) ?? [];
      existing.push(entry);
      map.set(c.propertyUID, existing);
    }
    const out = new Map<string, string>();
    map.forEach((entries, propertyUID) => {
      out.set(propertyUID, entries.sort().join('|'));
    });
    return out;
  }

  private fingerprintsEqual(a: Map<string, string>, b: Map<string, string>): boolean {
    if (a.size !== b.size) return false;
    for (const [k, v] of a) if (b.get(k) !== v) return false;
    return true;
  }

  /**
   * Force refresh of all properties
   */
  refreshProperties(): void {
    this.refreshTrigger$.next(true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearAllCache();
  }
} 